import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Database, Loader2, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import type { AgentAnalysisResult } from "@/types/communications";
import type { Agent } from "@/types/aiToolkit";
import type { AgentHandoffSource } from "@/types/agentHandoff";
import type { AgentActionType } from "@/lib/agentActionRouter";
import type { AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import { DiscussInChatButton } from "@/components/communications/DiscussInChatButton";

interface CommunicationAgentResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: AgentAnalysisResult[];
  agent?: Agent | null;
  handoffSource?: AgentHandoffSource;
  returnPath?: string;
  items?: AnalyzeItemInput[];
  actionType?: AgentActionType;
  onIndexResult?: (result: AgentAnalysisResult) => Promise<void>;
  onIndexAll?: (results: AgentAnalysisResult[]) => Promise<void>;
  onTrashMessage?: (messageId: string) => Promise<void>;
  canTrash?: boolean;
}

interface SpamVerdict {
  spam: boolean;
  confidence?: number;
  reason?: string;
}

function parseSpamVerdict(result: AgentAnalysisResult): SpamVerdict | null {
  const parsed = result.parsed as Record<string, unknown> | undefined;
  if (parsed && typeof parsed.spam === "boolean") {
    return {
      spam: parsed.spam,
      confidence: typeof parsed.confidence === "number" ? parsed.confidence : result.confidence,
      reason: typeof parsed.reason === "string" ? parsed.reason : undefined,
    };
  }
  return null;
}

function SpamVerdictCard({ verdict }: { verdict: SpamVerdict }) {
  const confidencePct =
    verdict.confidence != null ? `${Math.round(verdict.confidence * 100)}%` : null;

  return (
    <div
      className={`rounded-lg border p-4 space-y-2 ${
        verdict.spam ? "border-destructive/40 bg-destructive/5" : "border-green-500/40 bg-green-500/5"
      }`}
    >
      <div className="flex items-center gap-2">
        {verdict.spam ? (
          <ShieldAlert className="h-5 w-5 text-destructive" />
        ) : (
          <ShieldCheck className="h-5 w-5 text-green-600" />
        )}
        <span className="font-semibold">{verdict.spam ? "Spam" : "Not spam"}</span>
        {confidencePct && (
          <Badge variant="outline" className="text-xs">
            {confidencePct} confidence
          </Badge>
        )}
      </div>
      {verdict.reason && (
        <p className="text-sm text-muted-foreground">{verdict.reason}</p>
      )}
    </div>
  );
}

export function CommunicationAgentResultsDialog({
  open,
  onOpenChange,
  results,
  agent = null,
  handoffSource = "fireflies",
  returnPath = "/fireflies",
  items = [],
  actionType = "none",
  onIndexResult,
  onIndexAll,
  onTrashMessage,
  canTrash = true,
}: CommunicationAgentResultsDialogProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [indexingId, setIndexingId] = useState<string | null>(null);
  const [indexingAll, setIndexingAll] = useState(false);
  const [trashingId, setTrashingId] = useState<string | null>(null);
  const [confirmTrashId, setConfirmTrashId] = useState<string | null>(null);

  const handleCopy = useCallback(async (result: AgentAnalysisResult) => {
    try {
      await navigator.clipboard.writeText(result.content);
      setCopiedId(result.itemId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  }, []);

  const successResults = results.filter((r) => !r.error && r.content.trim());

  const handleIndex = useCallback(
    async (result: AgentAnalysisResult) => {
      if (!onIndexResult) return;
      setIndexingId(result.itemId);
      try {
        await onIndexResult(result);
      } finally {
        setIndexingId(null);
      }
    },
    [onIndexResult]
  );

  const handleIndexAll = useCallback(async () => {
    if (!onIndexAll || successResults.length === 0) return;
    setIndexingAll(true);
    try {
      await onIndexAll(successResults);
    } finally {
      setIndexingAll(false);
    }
  }, [onIndexAll, successResults]);

  const handleTrash = useCallback(
    async (messageId: string) => {
      if (!onTrashMessage) return;
      setTrashingId(messageId);
      try {
        await onTrashMessage(messageId);
        setConfirmTrashId(null);
      } finally {
        setTrashingId(null);
      }
    },
    [onTrashMessage]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Agent Analysis Results</DialogTitle>
            <DialogDescription>
              Review agent output, then index for AI chat retrieval if useful.
            </DialogDescription>
          </DialogHeader>
          {onIndexAll && successResults.length > 0 && (
            <Button
              size="sm"
              className="self-start"
              disabled={indexingAll || indexingId !== null}
              onClick={() => void handleIndexAll()}
            >
              {indexingAll ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Index all results for AI
            </Button>
          )}
          <ScrollArea className="flex-1 pr-4 -mr-4">
            <div className="space-y-4">
              {results.map((result) => {
                const spamVerdict = parseSpamVerdict(result);
                return (
                  <div key={result.itemId} className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm truncate">{result.itemLabel}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        {spamVerdict?.spam && onTrashMessage && canTrash && (
                          <Button
                            variant="destructive"
                            size="sm"
                            className="h-7"
                            disabled={trashingId === result.itemId}
                            onClick={() => setConfirmTrashId(result.itemId)}
                          >
                            {trashingId === result.itemId ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                            )}
                            Move to trash
                          </Button>
                        )}
                        {!result.error && onIndexResult && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-7"
                            disabled={indexingAll || indexingId === result.itemId}
                            onClick={() => void handleIndex(result)}
                          >
                            {indexingId === result.itemId ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                              <Database className="h-3.5 w-3.5 mr-1" />
                            )}
                            Index for AI
                          </Button>
                        )}
                        {!result.error && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 shrink-0"
                            onClick={() => void handleCopy(result)}
                          >
                            {copiedId === result.itemId ? (
                              <Check className="h-3.5 w-3.5 mr-1" />
                            ) : (
                              <Copy className="h-3.5 w-3.5 mr-1" />
                            )}
                            Copy
                          </Button>
                        )}
                      </div>
                    </div>
                    {result.error ? (
                      <Badge variant="destructive">{result.error}</Badge>
                    ) : spamVerdict ? (
                      <SpamVerdictCard verdict={spamVerdict} />
                    ) : (
                      <div className="text-sm whitespace-pre-wrap">{result.content}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 shrink-0 border-t">
            <DiscussInChatButton
              agent={agent}
              source={handoffSource}
              returnPath={returnPath}
              items={items}
              results={results}
              actionType={actionType}
              onIndexForRag={
                onIndexAll && successResults.length > 0
                  ? () => onIndexAll(successResults)
                  : undefined
              }
            />
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTrashId} onOpenChange={(o) => !o && setConfirmTrashId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move email to trash?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move the email to Gmail Trash. You can recover it from Trash within 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmTrashId && void handleTrash(confirmTrashId)}
            >
              Move to trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
